// Delivery scheduling rules (single source of truth).
//
// We offer TWO delivery days each week: Friday and Saturday.
//   - Friday delivery   → order cutoff is Thursday 10:00 PM (local)
//   - Saturday delivery → order cutoff is Friday  10:00 PM (local)
//
// Customers must pay at checkout. For delivery dates >= 7 days out we offer
// a PRE-ORDER plan: pay a deposit now, balance due by the normal cutoff.
//
// CLOSED_DATES are kitchen closures — those delivery slots are skipped entirely.

const FRIDAY = 5; // JS getDay() value: Sun=0 Mon=1 Tue=2 Wed=3 Thu=4 Fri=5 Sat=6
const SATURDAY = 6;

// Specific delivery dates we are CLOSED. Add new closures here as ISO YYYY-MM-DD.
// Keep in sync with backend `CLOSED_DELIVERY_DATES` in /app/backend/server.py.
export const CLOSED_DATES = new Set([
    "2026-06-05", // Friday, June 5, 2026 — kitchen closed
    "2026-06-06", // Saturday, June 6, 2026 — kitchen closed
]);

// A delivery date is "pre-order eligible" when it's >= PRE_ORDER_THRESHOLD_DAYS
// away. Inside that window, full payment at checkout is required.
export const PRE_ORDER_THRESHOLD_DAYS = 7;
export const PRE_ORDER_MIN_DEPOSIT_PCT = 10;
export const PRE_ORDER_DEPOSIT_OPTIONS = [10, 25, 50, 100];

// How many future Fri/Sat slot pairs to surface in the picker.
const SLOT_HORIZON_WEEKS = 4;

function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

function nextWeekday(from, weekday) {
    const d = startOfDay(from);
    const delta = (weekday - d.getDay() + 7) % 7;
    return addDays(d, delta === 0 ? 7 : delta);
}

function cutoffFor(deliveryDate, cutoffHourLocal = 22) {
    // The cutoff is the day BEFORE the delivery at 22:00 (10 PM) local time.
    const cutoff = addDays(deliveryDate, -1);
    cutoff.setHours(cutoffHourLocal, 0, 0, 0);
    return cutoff;
}

function fmtIso(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function fmtPretty(d) {
    return d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
    });
}

function daysBetween(a, b) {
    const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
    return Math.round(ms / 86400000);
}

function buildSlot(date, dayKind, now) {
    const iso = fmtIso(date);
    const daysAway = daysBetween(now, date);
    const isPreOrder = daysAway >= PRE_ORDER_THRESHOLD_DAYS;
    const cutoff = cutoffFor(date);
    const cutoffDayName = dayKind === "friday" ? "Thu" : "Fri";
    return {
        iso,
        label: dayKind === "friday" ? "Friday delivery" : "Saturday delivery",
        dateText: fmtPretty(date),
        cutoffText: `Order by ${cutoffDayName} ${cutoff.toLocaleString("en-US", { month: "short", day: "numeric" })} · 10 PM`,
        window: "11 AM – 8 PM",
        dayKind,
        isPreOrder,
        daysAway,
    };
}

export function computeDeliverySlots(now = new Date()) {
    const slots = [];
    let friday = nextWeekday(now, FRIDAY);
    if (now >= cutoffFor(friday)) friday = addDays(friday, 7);

    let saturday = nextWeekday(now, SATURDAY);
    if (now >= cutoffFor(saturday)) saturday = addDays(saturday, 7);

    // Walk forward SLOT_HORIZON_WEEKS weeks, skipping any CLOSED_DATES.
    for (let i = 0; i < SLOT_HORIZON_WEEKS; i++) {
        const f = addDays(friday, i * 7);
        if (!CLOSED_DATES.has(fmtIso(f))) slots.push(buildSlot(f, "friday", now));
        const s = addDays(saturday, i * 7);
        if (!CLOSED_DATES.has(fmtIso(s))) slots.push(buildSlot(s, "saturday", now));
    }

    // Return only the first 2 non-closed slots by default so the picker stays
    // simple, BUT keep all of them available if the first two are pre-order
    // (so the customer can also see the next "normal payment" slot).
    return slots;
}

export const DELIVERY_RULES = {
    friday_cutoff: "Thursday 10:00 PM",
    saturday_cutoff: "Friday 10:00 PM",
    window: "11 AM – 8 PM",
    days: ["Friday", "Saturday"],
};
